import type { Locale } from "./types";

export type { Locale } from "./types";

export const LOCALE_STORAGE_KEY = "ttrpg_landing_locale";

const ru = {
  meta: {
    title: "НРИ Плеер — редактор кампаний и пульт мастера для настольных RPG",
    description:
      "Десктопное приложение для мастеров НРИ: граф сцен, пульт управления, экран презентации, музыка и эффекты. Windows, macOS, Linux.",
    ogTitle: "НРИ Плеер — редактор кампаний и пульт мастера для настольных RPG",
    ogDescription:
      "Создавайте кампании и ведите партии. Редактор, пульт и экран презентации в одном приложении.",
  },
  brand: "НРИ Плеер",
  nav: {
    features: "Возможности",
    howItWorks: "Как это работает",
    platforms: "Платформы",
    pricing: "Тарифы",
    faq: "FAQ",
  },
  navAria: "Основная навигация",
  cta: {
    download: "Скачать",
    buy: "Поддержать",
    downloadFree: "Скачать бесплатно",
    buyLicense: "Поддержать проект",
    downloadForOs: "Скачать для моей ОС",
    vk: "ВК",
  },
  hero: {
    eyebrow: "Инструмент для мастеров · Платформа для историй",
    tagline: "Создавайте кампании и ведите партии",
    description:
      "Редактор кампаний и пульт мастера в одном приложении. Собирайте сюжет как граф сцен, ведите игру с пульта — игроки видят только атмосферу на втором экране.",
    platformsLine: "Windows · macOS · Linux · Русский и English",
    highlights: {
      offline: {
        title: "Офлайн",
        description: "Интернет нужен только для активации и обновлений",
      },
      secondScreen: {
        title: "Второй экран",
        description:
          "Игроки видят карту, видео и атмосферу без панелей редактора",
      },
    },
  },
  mockupWindows: {
    editor: "Редактор",
    control: "Пульт управления",
    presentation: "Экран презентации",
  },
  screenshotAlt: {
    editor: "Редактор кампаний НРИ Плеер — граф сцен и свойства сцены",
    control: "Пульт управления НРИ Плеер — переключение сцен и музыка",
    presentation: "Экран презентации НРИ Плеер — карта для игроков",
    atmosphere: "Атмосфера за столом — эффекты поля и инструменты мастера",
  },
  audience: {
    eyebrow: "Для мастеров, которые ведут историю",
    title: "Один инструмент для подготовки, стола и трансляции",
    cards: {
      gm: {
        title: "Мастер за столом",
        description:
          "Всё под рукой во время сессии: сцены, музыка, эффекты и текущая ветка партии.",
      },
      prep: {
        title: "Подготовка кампании",
        description:
          "Визуальная карта вместо десятков заметок: сцены, переходы и медиа складываются в граф.",
      },
      table: {
        title: "Онлайн и офлайн столы",
        description:
          "Чистый экран для игроков на проекторе, ТВ или втором мониторе — без интерфейса мастера.",
      },
    },
  },
  videoShowcase: {
    eyebrow: "Видео",
    title: "Посмотрите, как это работает за столом",
    subtitle:
      "Короткий обзор редактора, пульта и экрана для игроков — от подготовки кампании до живой сессии.",
    iframeTitle: "Видеообзор TTRPG Player",
    openOnVk: "Открыть видео на VK Video",
  },
  benefits: {
    title: "Ведите партию, а не переключайте вкладки",
    cards: {
      allInOne: {
        title: "Всё в одном окне мастера",
        description:
          "Сцены, музыка, эффекты и ветвления без сторонних плееров и таблиц.",
      },
      sync: {
        title: "Мгновенная синхронизация",
        description: "Переключение с пульта сразу обновляет экран игроков.",
      },
      immersion: {
        title: "Погружение без отвлечений",
        description:
          "На презентации нет панелей редактора, только карта и атмосфера.",
      },
      branching: {
        title: "Наглядное ветвление",
        description: "Граф сцен показывает развилки сюжета и прогресс партии.",
      },
      portable: {
        title: "Портативные проекты",
        description:
          "Одна папка кампании (.ttrpg.zip), удобно переносить между сессиями.",
      },
      pro: {
        title: "Профессиональный инструмент",
        description:
          "Десктопное приложение с лицензией, обновлениями и офлайн-работой.",
      },
    },
  },
  features: {
    title: "Четыре режима работы — одна партия",
    blocks: {
      editor: {
        title: "Редактор кампаний",
        description:
          "Планируйте кампанию как граф сцен: связывайте локации, закладывайте развилки сюжета, прикрепляйте карты и музыку — и собирайте материалы партии до первого броска костей.",
        tags: "Граф сцен · ветвления · медиа",
      },
      control: {
        title: "Пульт управления",
        description:
          "Ведите сессию из одного окна: переключайте сцены, следите за экраном игроков, держите ход сюжета под рукой и управляйте музыкой, пока стол ждёт вашего следующего хода.",
        tags: "Сцены · музыка · сюжетная линия",
      },
      presentation: {
        title: "Экран презентации",
        description:
          "Вынесите карту и атмосферу на второй монитор, проектор или ТВ — игроки видят только задуманное для сцены, без панелей редактора, с поворотом экрана под ваш стол.",
        tags: "Второй экран · карта · атмосфера",
      },
      atmosphere: {
        title: "Атмосфера за столом",
        description:
          "Оживите карту погодой и светом, дорисуйте от руки то, что видят игроки, и меняйте настроение сцены музыкой — от тихой таверны до грозы над полем боя.",
        tags: "Погода · свет · музыка сцены",
      },
    },
  },
  steps: {
    title: "Три шага до живой сцены",
    items: {
      create:
        "Создайте кампанию — добавьте сцены, карты, музыку и связи на графе.",
      launch: "Запустите игру — откроются пульт мастера и экран презентации.",
      run: "Ведите сессию — переключайте сцены, включайте эффекты и музыку в один клик.",
    },
  },
  platforms: {
    title: "Доступно на вашей ОС",
    cards: {
      windows: {
        title: "Windows",
        description: "Windows 10/11, 64-bit. Установщик (.exe).",
      },
      macos: {
        title: "macOS",
        description: "Intel (x64) и Apple Silicon (M1/M2/M3). DMG.",
      },
      linux: {
        title: "Linux",
        description: "x64 и ARM64. AppImage.",
      },
    },
    note: "Автообновления для лицензированных пользователей · Интерфейс на русском и английском · Работает офлайн",
    distributionDisclaimer:
      "Установочные файлы не подписаны сертификатом издателя и предоставляются «как есть» (as is), без каких‑либо явных или подразумеваемых гарантий. Разработчик не несёт ответственности за ущерб, связанный с загрузкой, установкой или использованием приложения. Устанавливая программу, вы принимаете все риски и ответственность за последствия на своей стороне.",
  },
  pricing: {
    title: "Выберите ключ мастера",
    subtitle:
      "Активируйте продуктовый ключ в приложении: Настройки → Указать ключ",
    recommendedBadge: "⭐ Рекомендуем",
    tiers: {
      basic: {
        title: "Базовый ключ",
        features: ["Доступ на 1 год · до 3 устройств"],
      },
      master: {
        title: "Ключ мастера",
        features: ["Доступ на 2 года · до 5 устройств"],
      },
      legend: {
        title: "Легенда НРИ",
        features: ["Доступ на 99 лет · до 15 устройств"],
      },
    },
    exchangeRateNote: "",
    footnote:
      "Ключ формата TTRPG-… · Привязка к устройству · Лицензионное соглашение — в футере сайта",
    supportNote:
      "Вы можете добровольно поддержать проект. В знак благодарности правообладатель вручную выдаёт продуктовый ключ TTRPG-… соответствующего уровня доступа.",
  },
  comparison: {
    title: "От разрозненных вкладок — к пульту мастера",
    beforeLabel: "Было",
    beforeText:
      "Заметки, Spotify, отдельные картинки, ручное переключение вкладок.",
    afterLabel: "Стало",
    afterText:
      "Единый пульт, синхронный экран игроков, граф кампании и встроенная атмосфера.",
  },
  faq: {
    title: "Вопросы перед первой сессией",
    items: {
      what: {
        question: "Что такое НРИ Плеер?",
        answer:
          "Десктопный редактор кампаний и пульт мастера для D&D, Pathfinder и других настольных RPG.",
      },
      internet: {
        question: "Нужен ли интернет во время игры?",
        answer:
          "Нет. Интернет нужен только для активации лицензии и обновлений.",
      },
      monitor: {
        question: "Можно ли использовать второй монитор?",
        answer:
          "Да. Экран презентации открывается отдельным окном для монитора, проектора или ТВ.",
      },
      activate: {
        question: "Как активировать лицензию?",
        answer:
          "В приложении откройте Настройки → Выбрать ключ и вставьте ключ формата TTRPG-….",
      },
      os: {
        question: "На каких ОС работает приложение?",
        answer:
          "Windows x64, macOS x64+arm64 и Linux x64+arm64 в формате AppImage.",
      },
    },
  },
  ctaBanner: {
    title: "Готовы вести следующую сессию по-новому?",
  },
  eulaModal: {
    title: "Лицензионное соглашение",
    localeNote: "",
    close: "Закрыть",
  },
  footer: {
    aria: "Подвал",
    links: {
      download: "Скачать",
      buy: "Поддержать",
      faq: "FAQ",
      enterKey: "Выбрать ключ",
      support: "Поддержка",
      vk: "ВКонтакте",
      eula: "Лицензия",
    },
  },
  localeSwitcher: {
    aria: "Язык сайта",
    ru: "RU",
    en: "EN",
  },
};

export type SiteCopy = typeof ru;

const en: SiteCopy = {
  meta: {
    title:
      "TTRPG Player — campaign editor and GM control panel for tabletop RPGs",
    description:
      "Desktop app for tabletop GMs: scene graph, control panel, presentation screen, music and effects. Windows, macOS, Linux.",
    ogTitle:
      "TTRPG Player — campaign editor and GM control panel for tabletop RPGs",
    ogDescription:
      "Build campaigns and run sessions. Editor, control panel and player screen in one app.",
  },
  brand: "TTRPG Player",
  nav: {
    features: "Features",
    howItWorks: "How it works",
    platforms: "Platforms",
    pricing: "Pricing",
    faq: "FAQ",
  },
  navAria: "Main navigation",
  cta: {
    download: "Download",
    buy: "Support",
    downloadFree: "Download free",
    buyLicense: "Support the project",
    downloadForOs: "Download for my OS",
    vk: "VK",
  },
  hero: {
    eyebrow: "A tool for GMs · A platform for stories",
    tagline: "Build campaigns and run your table",
    description:
      "Campaign editor and GM control panel in one desktop app. Map your story as a scene graph, run the session from the panel — players see only the atmosphere on a second screen.",
    platformsLine: "Windows · macOS · Linux · Russian and English UI",
    highlights: {
      offline: {
        title: "Offline",
        description: "Internet is only needed for activation and updates",
      },
      secondScreen: {
        title: "Second screen",
        description:
          "Players see maps, video and atmosphere without editor panels",
      },
    },
  },
  mockupWindows: {
    editor: "Editor",
    control: "Control panel",
    presentation: "Presentation screen",
  },
  screenshotAlt: {
    editor: "TTRPG Player campaign editor — scene graph and scene properties",
    control: "TTRPG Player control panel — scene switching and music",
    presentation: "TTRPG Player presentation screen — map for players",
    atmosphere: "Table atmosphere — field effects and GM tools",
  },
  audience: {
    eyebrow: "For GMs who run the story",
    title: "One tool for prep, the table and the big screen",
    cards: {
      gm: {
        title: "GM at the table",
        description:
          "Everything at hand during a session: scenes, music, effects and the party’s current branch.",
      },
      prep: {
        title: "Campaign prep",
        description:
          "A visual map instead of dozens of notes: scenes, transitions and media in one graph.",
      },
      table: {
        title: "In-person and hybrid tables",
        description:
          "A clean screen for players on a projector, TV or second monitor — no GM UI.",
      },
    },
  },
  videoShowcase: {
    eyebrow: "Video",
    title: "See it in action at your table",
    subtitle:
      "A quick tour of the editor, control panel and player screen — from campaign prep to a live session.",
    iframeTitle: "TTRPG Player video overview",
    openOnVk: "Open video on VK Video",
  },
  benefits: {
    title: "Run the session, don’t juggle tabs",
    cards: {
      allInOne: {
        title: "Everything in one GM window",
        description:
          "Scenes, music, effects and branches without third-party players or spreadsheets.",
      },
      sync: {
        title: "Instant sync",
        description:
          "Switching from the panel updates the player screen immediately.",
      },
      immersion: {
        title: "Immersion without clutter",
        description:
          "The presentation shows no editor chrome — only the map and atmosphere.",
      },
      branching: {
        title: "Clear branching",
        description: "The scene graph shows story forks and party progress.",
      },
      portable: {
        title: "Portable projects",
        description:
          "One campaign folder (.ttrpg.zip), easy to move between sessions.",
      },
      pro: {
        title: "Professional desktop tool",
        description: "Desktop app with licensing, updates and offline play.",
      },
    },
  },
  features: {
    title: "Four modes — one session",
    blocks: {
      editor: {
        title: "Campaign editor",
        description:
          "Plan your campaign as a scene graph: link locations, lay out story branches, attach maps and music — and gather session materials before the first roll.",
        tags: "Scene graph · branching · media",
      },
      control: {
        title: "Control panel",
        description:
          "Run the session from one window: switch scenes, watch the player screen, keep the story beat at hand and control music while the table waits for your next move.",
        tags: "Scenes · music · story line",
      },
      presentation: {
        title: "Presentation screen",
        description:
          "Send the map and atmosphere to a second monitor, projector or TV — players see only what the scene calls for, with no editor panels and rotation to fit your table setup.",
        tags: "Second screen · map · atmosphere",
      },
      atmosphere: {
        title: "Atmosphere at the table",
        description:
          "Bring the map to life with weather and light, sketch what players see by hand, and shift the mood with scene music — from a quiet tavern to a storm over the battlefield.",
        tags: "Weather · light · scene music",
      },
    },
  },
  steps: {
    title: "Three steps to a live scene",
    items: {
      create:
        "Create a campaign — add scenes, maps, music and links on the graph.",
      launch: "Launch the game — the GM panel and presentation screen open.",
      run: "Run the session — switch scenes, effects and music in one click.",
    },
  },
  platforms: {
    title: "Available on your OS",
    cards: {
      windows: {
        title: "Windows",
        description: "Windows 10/11, 64-bit. Installer (.exe).",
      },
      macos: {
        title: "macOS",
        description: "Intel (x64) and Apple Silicon (M1/M2/M3). DMG.",
      },
      linux: {
        title: "Linux",
        description: "x64 and ARM64. AppImage.",
      },
    },
    note: "Auto-updates for licensed users · Russian and English UI · Works offline",
    distributionDisclaimer:
      "Installers are not code-signed by the publisher and are provided “as is”, without express or implied warranties. The developer is not liable for any damage arising from downloading, installing or using the application. By installing the software, you assume all risks and responsibility on your side.",
  },
  pricing: {
    title: "Choose your GM key",
    subtitle: "Activate your product key in the app: Settings → Choose key",
    recommendedBadge: "⭐ Recommended",
    tiers: {
      basic: {
        title: "Basic key",
        features: ["1 year · up to 3 devices"],
      },
      master: {
        title: "GM key",
        features: ["2 years · up to 5 devices"],
      },
      legend: {
        title: "TTRPG Legend",
        features: ["99 years · up to 15 devices"],
      },
    },
    exchangeRateNote:
      "USD equivalent at the current RUB exchange rate (updated when you open the site).",
    footnote:
      "Key format TTRPG-… · Device-bound · License agreement in the site footer",
    supportNote:
      "You can voluntarily support the project. As a thank-you, the rights holder manually issues a TTRPG-… product key for the corresponding access tier.",
  },
  comparison: {
    title: "From scattered tabs to a GM panel",
    beforeLabel: "Before",
    beforeText: "Notes, Spotify, separate images, manual tab switching.",
    afterLabel: "After",
    afterText:
      "One panel, synced player screen, campaign graph and built-in atmosphere.",
  },
  faq: {
    title: "Questions before your first session",
    items: {
      what: {
        question: "What is TTRPG Player?",
        answer:
          "A desktop campaign editor and GM control panel for D&D, Pathfinder and other tabletop RPGs.",
      },
      internet: {
        question: "Do I need internet during play?",
        answer:
          "No. Internet is only required for license activation and updates.",
      },
      monitor: {
        question: "Can I use a second monitor?",
        answer:
          "Yes. The presentation screen opens in a separate window for a monitor, projector or TV.",
      },
      activate: {
        question: "How do I activate a license?",
        answer:
          "In the app, open Settings → Choose key and paste a key in the TTRPG-… format.",
      },
      os: {
        question: "Which operating systems are supported?",
        answer: "Windows x64, macOS x64+arm64 and Linux x64+arm64 as AppImage.",
      },
    },
  },
  ctaBanner: {
    title: "Ready to run your next session differently?",
  },
  eulaModal: {
    title: "End User License Agreement",
    localeNote: "The binding legal text below is in Russian.",
    close: "Close",
  },
  footer: {
    aria: "Footer",
    links: {
      download: "Download",
      buy: "Support",
      faq: "FAQ",
      enterKey: "Choose key",
      support: "Support",
      vk: "VK",
      eula: "EULA",
    },
  },
  localeSwitcher: {
    aria: "Site language",
    ru: "RU",
    en: "EN",
  },
};

export const MESSAGES: Record<Locale, SiteCopy> = { ru, en };

export function normalizeLocale(raw: string | null | undefined): Locale {
  if (raw === "en") return "en";
  return "ru";
}

export function inferLocaleFromLanguages(langs: readonly string[]): Locale {
  for (const lang of langs) {
    const tag = lang.trim().toLowerCase().split(/[-_]/u)[0];
    if (tag === "ru") return "ru";
  }
  return "en";
}

export function inferLocaleFromNavigator(): Locale {
  if (typeof navigator === "undefined") return "en";
  const langs = [...navigator.languages, navigator.language].filter(Boolean);
  return inferLocaleFromLanguages(langs);
}
